/**
 * Desk Store - Zustand store for Desk UI state management
 *
 * Use this store for all Desk-related state instead of React Context.
 * Benefits: selective re-renders, built-in persistence, no provider nesting.
 *
 * @example
 * // In a component - only re-renders when these specific values change
 * const selectedMatterId = useDeskStore((state) => state.selectedMatterId)
 * const selectMatter = useDeskStore((state) => state.selectMatter)
 */

import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'
import type { AttachedClauseRef } from '../utils/attachedClauseFormat'
import type { Citation } from '../components/chat/types'
// Plan 42 Task 8 — runtime-only cross-store reference so selectMatter can
// atomically swap document tabs via documentStore.switchMatter. ES modules
// tolerate this circular dep because documentStore.ts is only read via
// getState() inside the action body (lazy), never at module init.
import { useDocumentStore } from './documentStore'

// Panel size constraints (percentages for react-resizable-panels)
const PANEL_MIN_SIZE = 10 // 10% minimum
const PANEL_MAX_SIZE = 35 // 35% maximum
const PANEL_DEFAULT_LEFT = 18 // ~280px at 1600px width
const PANEL_DEFAULT_RIGHT = 20 // ~320px at 1600px width

// Category collapse state type
export interface CategoryCollapseStates {
  matter: boolean
  workbench: boolean
  playbook: boolean
  workflow: boolean
}

// Section order type for drag-drop reordering (Task 213)
export type SectionCategory = 'matter' | 'workbench' | 'playbook' | 'workflow'
export const DEFAULT_SECTION_ORDER: SectionCategory[] = ['matter', 'workbench', 'playbook', 'workflow']

// Right panel section state (Task 214)
export interface RightPanelSectionState {
  historySlideOutOpen: boolean
  quickActionsCollapsed: boolean
}

// Task 214.1: Right panel context-driven views
export type RightPanelViewType =
  | 'chat'              // Default: general chat + quick actions
  | 'clauseActions'     // Clause selected: Explain, Redraft, Add comment
  | 'negotiationDetail' // Task 302: Negotiation finding clicked
  | 'redraft'           // Redraft interface (from any context)
  | 'playbookMatches'   // Plan 110: Match selection list
  | 'playbookDetail'    // Plan 110: Selected playbook clause working view
  | 'playbookStaging'   // Plan 110 Task 15: Side-by-side Modify/Replace staging
  | 'playbookComment'   // Plan 110 Task 16: Draft comment staging with tone/style
  | 'alignStaging'      // Plan 152: Align operation staging (workbench/playbook/compare_finding target)
  | 'commentDetail'     // Plan 170: Word comment thread detail
  | 'clausePairCompare' // Plan 178: transient view while pair compare LLM runs / no-change result

// Context data associated with each right panel view
export interface ClauseContext {
  clauseId: string
  clauseTitle: string
  clauseText: string
  documentId?: string
  documentName?: string
}

export interface IssueContext {
  issueId: string
  issueTitle: string
  severity: string
  clauseRef?: string
  description?: string
  riskAnalysis?: string
  recommendation?: string
  structuredRecommendations?: any[] | null
}

export interface IssueChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isLoading?: boolean;
  // Redraft accept/reject flow
  isRedraft?: boolean;
  originalText?: string;
  redraftedText?: string;
  redraftStatus?: 'pending' | 'accepted' | 'rejected';
  // Plan 175 — chat-thread persist-on-generate stores the row id here.
  // Accept/Reject status-flip this row instead of creating a new one.
  // Absent when persistence failed at generate time (see persistError).
  revisionId?: string;
  // Plan 175 — user-visible "not saved to history" banner trigger. The
  // diff still renders; only the version-history thread is missing.
  persistError?: string;
  // Issue #761: inline `[N]` markers in assistant turns need the backing
  // citation rows so the per-clause/per-issue/per-finding threads can render
  // hover + click identically to the general chat. Captured from
  // response.assistant_message.citations and chat_messages.citations on
  // hydration. Absent on user turns and on redraft turns (the redraft prompt
  // does not return citations).
  citations?: Citation[];
  // Plan 35 — server-issued chat_messages.id for this row. Stamped on both
  // hydrated rows (from GET /chat/sessions/:id/messages) and live rows (from
  // response.assistant_message.id). Used as the Tier 2 join key in the
  // restampHydratedRedrafts helper so Accept/Reject UI survives reload even
  // when local user bubbles were suppressed at send time.
  serverMessageId?: string;
}

// Plan 172: chip-initiated prompts split into a short, user-facing label
// (persisted as chat_messages.content) and a verbose directive routed through
// context_injection (not persisted, not replayed on future turns).
export interface PendingChatPrompt {
  /** Short user-facing label persisted as chat_messages.content. */
  content: string;
  /** Verbose directive sent via context_injection — NOT persisted into content. */
  instructionInjection?: string;
  /** Suppress the local user-message bubble (response renders its own UI). */
  suppressUserBubble?: boolean;
  /**
   * Issue #784: per-user-intent idempotency key. Set by PromptChips via
   * `useMemo(crypto.randomUUID, [])` so the same chip-block mount carries
   * the same key across any duplicate dispatch. Forwarded by
   * IssueChatThread.sendMessage to apiService.sendChatMessage as the
   * `Idempotency-Key` HTTP header. See debugging-plans/36-* for the full
   * duplicate-handling contract.
   */
  idempotencyKey?: string;
}

export interface ChangeContext {
  findingId: string
  changeType: string
  originalText?: string
  modifiedText?: string
  clauseRef?: string
  // Task 242: Additional fields for compare overlay
  sourceAnalysisRunId?: string
  differenceDescription?: string
  riskAssessment?: string
  businessImpact?: string
  recommendation?: string
  legalSignificance?: string
  // Plan 103: Alignment confidence fields
  matchConfidence?: number
  alignmentType?: string
  nearTie?: boolean
  toolLimitHit?: boolean
  // Plan 235 / #967: whether a redraft for this finding can be APPLIED to the
  // document. Resolved from the finding TYPE by buildFindingContext, never
  // inferred from prompt text. Deletions and unknown types are 'draft-only'.
  redraftMode?: 'applicable' | 'draft-only'
}

export interface RedraftContext {
  clauseId: string
  clauseTitle: string
  originalText: string
  source: 'clause' | 'issue' | 'compare' | 'negotiation'
  sourceContext?: string // Issue summary, change summary, etc.
  documentId?: string
  // Plan 175 — required for thread-scoping. Set whichever applies; manual
  // clause redrafts (source='clause') leave both null.
  sourceIssueId?: string
  sourceFindingId?: string
}

// Task 302: Context for negotiation finding detail view
export interface NegotiationContext {
  findingId: string
  analysisRunId: string
  findingTitle: string
  severity: string
  findingType: string
  changeSummary?: string
  commentSummary?: string
  riskAnalysis?: string
  recommendation?: string
  recommendationStrength?: string
  findingStatus?: string
  clauseId?: string
  clauseText?: string
  // Plan 196: playbook provenance for right-pane attribution block.
  // sourcePositionId is the lookup key for the new attribution endpoint;
  // sourcePlaybookId is for the deep-link to /playbook-builder/:id.
  // matchedPlaybookTier / matchedPlaybookClauseType are FE-readable
  // mirrors of agent_context fields so the right-pane can render
  // without an extra round trip.
  sourcePlaybookId?: string | null
  sourcePositionId?: string | null
  matchedPlaybookTier?: 'house' | 'first_fallback' | 'non_negotiable' | null
  matchedPlaybookClauseType?: string | null
}

// Plan 110 Task 15: Playbook staging context for Modify/Replace
export interface PlaybookStagingContext {
  matchId: string
  clauseId: string // document clause ID
  action: 'modify' | 'replace'
  originalText: string
  positionType?: string
  compareRecommendation?: string // from playbook compare, for modify action prompt selection
}

// Plan 110 Task 16: Playbook comment context for Draft Comment
export interface PlaybookCommentContext {
  matchId: string
  clauseId: string
  positionType?: string
  redraftedText?: string // If Modify/Replace was run first
}

// Plan 152: Align operation context. The *Id fields are sent to the server;
// the *Text fields are client-side caches for the side-by-side render and
// are NEVER on the wire — the server loads target text by id, decrypts, and
// ignores anything client-supplied.
export type AlignTarget =
  | {
      source: 'playbook'
      matchId: string                 // clause_playbook_matches.id (mark-reviewed on accept)
      positionId: string              // playbook_positions.id (UUID) — what TargetLoader needs
      positionType: 'house' | 'first_fallback' | 'non_negotiable'
      positionText: string            // client cache; never sent
    }
  | {
      source: 'workbench'
      workbenchClauseId: string       // workbench_clauses.id (UUID)
      workbenchDocumentId: string
      targetText: string              // client cache
      targetHeading: string | null
    }
  | {
      source: 'compare_finding'
      findingId: string               // comparison_findings.id (UUID) → target_source_id on the wire
      baseClauseId: string            // base document_clauses.clause_id (compound) — client cache for diff render
      targetText: string              // client cache
    }

export interface AlignContext {
  /** Compound document_clauses.clause_id (text), matching frontend Clause.id. */
  matterClauseId: string
  matterClauseTitle: string
  matterClauseText: string            // client-side cache for side-by-side render
  documentId: string
  matterId: string                    // required for ownership context server-side
  target: AlignTarget
  preset: 'substantive' | 'verbatim'
  /** Compare-source only: pre-parsed recommendations for tick-box selection */
  compareRecommendations?: { all: string[]; selected: number[] }
  /** Optional refine instructions seeded from caller */
  initialUserInstructions?: string
}

export interface IssueListContext {
  issues: IssueContext[]
  clauseContext: ClauseContext
}

// Plan 170: Context for the Comment thread detail view.
// Kept lean — the detail view fetches the full thread from React Query
// rather than receiving it through context (matches the IssueContext pattern).
export interface CommentContext {
  commentId: string         // parent comment uuid
  documentId: string
  clauseId: string | null   // null for unmapped comments
  clauseTitle?: string      // displayed in the breadcrumb
}

export interface RightPanelView {
  type: RightPanelViewType
  clauseContext?: ClauseContext
  issueContext?: IssueContext
  issueListContext?: IssueListContext
  changeContext?: ChangeContext
  negotiationContext?: NegotiationContext
  redraftContext?: RedraftContext
  // Plan 110: Playbook matching context
  playbookMatchClauseId?: string  // document clause ID being matched
  playbookMatchId?: string        // selected match ID for detail view
  // Plan 110 Task 15: Playbook staging context
  playbookStagingContext?: PlaybookStagingContext
  // Plan 110 Task 16: Playbook comment context
  playbookCommentContext?: PlaybookCommentContext
  // Plan 152: Align staging context
  alignContext?: AlignContext
  // Plan 170: Comment thread detail context
  commentContext?: CommentContext
  // Plan 178: Clause-pair compare transient context (no-change result or
  // error). Loading state lives in the mutation hook, not here.
  clausePairCompareContext?: ClausePairCompareContext
}

// Plan 178: Right-panel context for the transient ClausePairCompareView.
// Pushed immediately on Continue (modal closes) so the user sees a loading
// state in the right pane while the LLM runs. The view fires the mutation
// itself; on 'finding' result it pushes changeDetail; on 'no_substantive_change'
// it switches to its inline summary state.
export interface ClausePairCompareContext {
  matterId: string
  reviewClauseId: string         // compound clause_id (matter side)
  reviewClauseTitle: string
  reviewClauseText: string       // Plan 38 follow-up: needed to build a full ClauseContext
                                 // for setFocusedFinding so FindingChatThread can redraft.
  reviewDocumentId: string
  baseClauseId: string           // compound clause_id (other doc in same matter)
  baseClauseHeading: string
}

// Task 232: Split pane mode type
export type SplitMode = 'none' | 'side-by-side' | 'top-down'

// Task 232: View mode for split panes (clause cards vs original)
export type DocumentViewMode = 'cards' | 'original'

// Task 234: Display options state
export type PageZoom = 75 | 90 | 100 | 110 | 125 | 150

export interface DisplayOptions {
  pageZoom: PageZoom
}

export const DEFAULT_DISPLAY_OPTIONS: DisplayOptions = {
  pageZoom: 100,
}

// Task 233.1: Clause cards filter type
export type ClauseCardsFilter = 'all' | 'issues' | 'changes' | 'comments'

// Task 235: Layout mode type
export type LayoutMode = 'default' | 'focus' | 'simplified'

// Chat session for right panel (Task 214/216)
export interface ChatSessionInfo {
  id: string
  name: string
}

export interface DeskStore {
  // Panel states
  leftPanelCollapsed: boolean
  rightPanelCollapsed: boolean
  leftPanelSize: number // percentage (0-100)
  rightPanelSize: number // percentage (0-100)

  // User ownership — used to clear stale state when a different user logs in
  persistedUserId: string | null

  // Matter context (UI calls them "matters", API uses "analysis_runs" for workflows)
  selectedMatterId: string | null
  activeAnalysisRunId: string | null // The active workflow within the selected matter

  // View mode
  viewMode: 'default' | 'focus' | 'simplified'

  // Task 232: Split pane state
  splitMode: SplitMode
  splitSecondaryDocumentId: string | null
  documentViewMode: DocumentViewMode
  preSplitLeftPanelCollapsed: boolean // To restore after exiting split
  // Issue #353: Which pane receives tab clicks
  focusedPane: 'primary' | 'secondary'

  // Document category collapse states (Task 206)
  categoryCollapseStates: CategoryCollapseStates

  // Section order for drag-drop reordering (Task 213)
  sectionOrder: SectionCategory[]

  // Right panel state (Task 214)
  rightPanelSections: RightPanelSectionState
  activeChatSessionId: string | null
  availableChatSessions: ChatSessionInfo[] // Populated by useMatterChat hook

  // Actions
  setPersistedUserId: (userId: string | null) => void
  selectMatter: (id: string | null) => void
  selectAnalysisRun: (id: string | null) => void
  toggleLeftPanel: () => void
  toggleRightPanel: () => void
  setLeftPanelSize: (size: number) => void
  setRightPanelSize: (size: number) => void
  setViewMode: (mode: 'default' | 'focus' | 'simplified') => void
  resetPanelStates: () => void
  toggleCategoryCollapse: (category: keyof CategoryCollapseStates) => void
  // Task 213: Section reordering
  setSectionOrder: (order: SectionCategory[]) => void
  resetSectionOrder: () => void

  // Task 214: Right panel actions
  toggleHistorySlideOut: () => void
  setHistorySlideOutOpen: (open: boolean) => void
  toggleQuickActionsCollapsed: () => void
  setActiveChatSession: (sessionId: string | null) => void
  setAvailableChatSessions: (sessions: ChatSessionInfo[]) => void

  // Task 214.1: Right panel navigation stack
  rightPanelViewStack: RightPanelView[]
  pushRightPanelView: (view: RightPanelView) => void
  popRightPanelView: () => void
  resetRightPanelToChat: () => void
  replaceRightPanelView: (view: RightPanelView) => void
  // Plan 41: centralised cleanup of right-pane state when a finding is
  // dismissed. Idempotent (does not write when nothing matches). Call sites:
  // every dismiss handler in DocumentViewer, SplitDocumentView, and the
  // three in-pane detail-view Dismiss buttons.
  clearRightPaneForDismissedFinding: (
    kind: 'issue' | 'compare' | 'negotiation',
    id: string,
  ) => void

  // Per-issue focus and chat threads
  activeRightPanelTab: 'chat' | 'analysis'
  setActiveRightPanelTab: (tab: 'chat' | 'analysis') => void
  focusedIssueId: string | null
  focusedIssueContext: IssueContext | null
  focusedFindingId: string | null
  focusedFindingContext: ChangeContext | null
  focusedFindingClauseContext: ClauseContext | null
  focusedClauseContext: ClauseContext | null
  // Plan 172: chip-initiated prompts carry a short user-visible label
  // (persisted as chat_messages.content) plus an optional verbose instruction
  // routed through context_injection. suppressUserBubble is true when the
  // assistant response renders its own UI (e.g. redraft accept/reject diff)
  // so the local user bubble would be redundant.
  pendingIssuePrompt: PendingChatPrompt | null
  pendingFindingPrompt: string | null
  issueChatThreads: Record<string, IssueChatMessage[]>
  findingChatThreads: Record<string, IssueChatMessage[]> // findingId -> messages (reuses IssueChatMessage type)
  // Plan 143: per-clause Analysis chat threads (clauseId -> messages).
  clauseChatThreads: Record<string, IssueChatMessage[]>
  acceptedRedrafts: Record<string, { text: string; revisionId: string; acceptedAt?: string }[]> // clauseId -> version stack (latest last)
  setFocusedIssue: (issueId: string | null, issueCtx: IssueContext | null, clauseCtx: ClauseContext | null) => void
  setFocusedFinding: (findingId: string | null, findingCtx: ChangeContext | null, clauseCtx: ClauseContext | null) => void
  sendIssuePrompt: (prompt: PendingChatPrompt) => void
  sendFindingPrompt: (prompt: string) => void
  clearPendingIssuePrompt: () => void
  clearPendingFindingPrompt: () => void
  addIssueChatMessage: (issueId: string, message: IssueChatMessage) => void
  addFindingChatMessage: (findingId: string, message: IssueChatMessage) => void
  // Issue #761: hydrate the per-issue / per-finding threads from GET
  // /messages on mount, mirroring `setClauseChatThread`. Required so the
  // scoped-chat citation tooltips survive page reload.
  setIssueChatThread: (issueId: string, messages: IssueChatMessage[]) => void
  setFindingChatThread: (findingId: string, messages: IssueChatMessage[]) => void
  // Plan 143: per-clause chat thread actions.
  addClauseChatMessage: (clauseId: string, message: IssueChatMessage) => void
  setClauseChatThread: (clauseId: string, messages: IssueChatMessage[]) => void
  acceptRedraft: (clauseId: string, newText: string, revisionId: string, acceptedAt?: string) => void
  revertRedraft: (clauseId: string) => void
  revertToVersion: (clauseId: string, versionIndex: number) => void // -1 = original, 0+ = stack index
  updateRedraftStatus: (issueId: string, messageId: string, status: 'accepted' | 'rejected' | 'pending') => void

  // Task 232: Split pane actions
  setSplitMode: (mode: SplitMode) => void
  setSplitSecondaryDocument: (documentId: string | null) => void
  setDocumentViewMode: (mode: DocumentViewMode) => void
  exitSplitMode: () => void
  // Issue #353: Pane focus for tab routing
  setFocusedPane: (pane: 'primary' | 'secondary') => void

  // Task 243: Compare side-by-side convenience action
  enterCompareSideBySide: (baseDocId: string, reviewDocId: string) => void

  // Task 487: Negotiation global view mode
  negotiationViewMode: 'incoming' | 'response'
  setNegotiationViewMode: (mode: 'incoming' | 'response') => void

  // Task 233.1: Clause cards filter
  clauseCardsFilter: ClauseCardsFilter
  setClauseCardsFilter: (filter: ClauseCardsFilter) => void

  // Task 234: Display options
  displayOptions: DisplayOptions
  setDisplayOption: <K extends keyof DisplayOptions>(key: K, value: DisplayOptions[K]) => void
  resetDisplayOptions: () => void

  // Task 235: Apply layout preset (adjusts panels)
  applyLayout: (mode: LayoutMode) => void

  // Plan 110: Playbook inspector sticky mode
  playbookInspectorLocked: boolean
  togglePlaybookInspector: () => void

  // Plan 110: Playbook compare results keyed by document clause ID
  playbookCompareResults: Record<string, { playbookName: string; clauseName?: string; summary: string; risk_analysis: string; recommendation: string }>
  setPlaybookCompareResult: (clauseId: string, result: { playbookName: string; clauseName?: string; summary: string; risk_analysis: string; recommendation: string }) => void
  clearPlaybookCompareResult: (clauseId: string) => void

  // Plan 110: Pending playbook comments for manifest integration (keyed by clause ID)
  pendingPlaybookComments: Record<string, { matchId: string; commentText: string; tone: string; style: string; createdAt: string }>
  addPendingPlaybookComment: (clauseId: string, entry: { matchId: string; commentText: string; tone: string; style: string }) => void
  removePendingPlaybookComment: (clauseId: string) => void

  // Plan 147: attached clauses for general-chat composer (session-only,
  // cleared on matter switch — see selectMatter)
  attachedClauses: AttachedClauseRef[]
  attachClause: (ref: AttachedClauseRef) => void
  removeAttachedClause: (clauseId: string) => void
  clearAttachedClauses: () => void
}

export const useDeskStore = create<DeskStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        persistedUserId: null,
        leftPanelCollapsed: false,
        rightPanelCollapsed: false,
        leftPanelSize: PANEL_DEFAULT_LEFT,
        rightPanelSize: PANEL_DEFAULT_RIGHT,
        selectedMatterId: null,
        activeAnalysisRunId: null,
        viewMode: 'default',
        // Task 232: Split pane initial state
        splitMode: 'none',
        splitSecondaryDocumentId: null,
        documentViewMode: 'cards',
        preSplitLeftPanelCollapsed: false,
        focusedPane: 'primary',
        categoryCollapseStates: {
          matter: false,
          workbench: false,
          playbook: false,
          workflow: false,
        },
        sectionOrder: DEFAULT_SECTION_ORDER,

        // Task 214: Right panel state
        rightPanelSections: {
          historySlideOutOpen: false,
          quickActionsCollapsed: false,
        },
        activeChatSessionId: null,
        availableChatSessions: [],

        // Task 214.1: Right panel navigation stack (chat is default, stack starts empty)
        rightPanelViewStack: [{ type: 'chat' }] as RightPanelView[],

        // Per-issue focus state (session-scoped, not persisted)
        activeRightPanelTab: 'chat' as 'chat' | 'analysis',
        focusedIssueId: null,
        focusedIssueContext: null,
        focusedFindingId: null,
        focusedFindingContext: null,
        focusedFindingClauseContext: null,
        focusedClauseContext: null,
        pendingIssuePrompt: null,
        pendingFindingPrompt: null,
        issueChatThreads: {} as Record<string, IssueChatMessage[]>,
        findingChatThreads: {} as Record<string, IssueChatMessage[]>,
        clauseChatThreads: {} as Record<string, IssueChatMessage[]>,
        acceptedRedrafts: {} as Record<string, { text: string; revisionId: string; acceptedAt?: string }[]>,

        // Task 487: Negotiation global view mode
        negotiationViewMode: 'incoming' as 'incoming' | 'response',

        // Task 233.1: Clause cards filter
        clauseCardsFilter: 'all' as ClauseCardsFilter,

        // Task 234: Display options
        displayOptions: DEFAULT_DISPLAY_OPTIONS,

        // Plan 110: Playbook inspector sticky mode
        playbookInspectorLocked: false,

        // Plan 110: Playbook compare results
        playbookCompareResults: {},
        pendingPlaybookComments: {},

        // Plan 147: attached clauses for general-chat composer
        attachedClauses: [] as AttachedClauseRef[],

        // Actions
        setPersistedUserId: (userId) => set({ persistedUserId: userId }, false, 'setPersistedUserId'),
        // INVARIANT (Plan 42 Task 8): selectMatter is the SOLE legal mutator
        // of `selectedMatterId`. It synchronously calls
        // documentStore.switchMatter so that, under React 18 batching, both
        // store updates land in one render commit — child renders observe a
        // single combined transition rather than the intermediate
        // selectedMatterId=new + openDocuments=old state. Any direct
        // `set({ selectedMatterId: ... })` call bypasses the swap and
        // re-introduces the orchestration gap (issue #807).
        selectMatter: (id) => {
          const prev = useDeskStore.getState().selectedMatterId
          set(
            // The right-panel view stack carries its own embedded clauseContext /
            // issueContext / etc. (RightPanelContainer reads currentView.clauseContext
            // directly, not focusedClauseContext). Clearing the focus fields without
            // resetting the stack leaves the previous matter's clauseActions view
            // rendered with its frozen data after a matter switch or delete.
            { selectedMatterId: id, activeAnalysisRunId: null, focusedIssueId: null, focusedIssueContext: null, focusedFindingId: null, focusedFindingContext: null, focusedFindingClauseContext: null, focusedClauseContext: null, pendingIssuePrompt: null, pendingFindingPrompt: null, issueChatThreads: {} as Record<string, IssueChatMessage[]>, findingChatThreads: {} as Record<string, IssueChatMessage[]>, clauseChatThreads: {} as Record<string, IssueChatMessage[]>, acceptedRedrafts: {} as Record<string, { text: string; revisionId: string; acceptedAt?: string }[]>, attachedClauses: [] as AttachedClauseRef[], rightPanelViewStack: [{ type: 'chat' }] as RightPanelView[], activeRightPanelTab: 'chat' as const },
            false,
            'selectMatter'
          )
          // Plan 42 Task 8 — atomic with the matter-id update. NOT data-model
          // atomic across the two Zustand stores: subscriber callbacks that
          // read cross-store state via getState() between these two sets see
          // the intermediate. Defence boundary:
          //   - Task 1 filters: subscriber-window opens are dropped, not
          //     persisted (save uses fromMatterId; restore replaces openDocs).
          //   - openDocument guard: catches stamp errors but NOT this case —
          //     selectedMatterId-sourced matterId already matches the new
          //     matter by the time the subscriber fires.
          // Subscriber-callback opens during the window are therefore silently
          // dropped (no contamination, no preservation). Acceptable because
          // most opens flow through batched React event handlers.
          //
          // selectMatter(null) case: passes '' to switchMatter. switchMatter's
          // contract treats falsy toMatterId as "clear live tabs without
          // restoring", never indexing matterTabState[''].
          if (id !== prev) {
            useDocumentStore.getState().switchMatter(prev || '', id || '')
          }
        },

        // Plan 147: attachedClauses actions
        attachClause: (ref) => set(
          (state) => {
            const idx = state.attachedClauses.findIndex((c) => c.clauseId === ref.clauseId)
            if (idx >= 0) {
              // P3: refresh snapshot — covers post-redraft re-attach
              const next = state.attachedClauses.slice()
              next[idx] = ref
              return { attachedClauses: next }
            }
            return { attachedClauses: [...state.attachedClauses, ref] }
          },
          false,
          'attachClause'
        ),
        removeAttachedClause: (clauseId) => set(
          (state) => ({
            attachedClauses: state.attachedClauses.filter((c) => c.clauseId !== clauseId),
          }),
          false,
          'removeAttachedClause'
        ),
        clearAttachedClauses: () => set({ attachedClauses: [] as AttachedClauseRef[] }, false, 'clearAttachedClauses'),

        selectAnalysisRun: (id) => set(
          { activeAnalysisRunId: id },
          false,
          'selectAnalysisRun'
        ),

        toggleLeftPanel: () => set(
          (state) => ({ leftPanelCollapsed: !state.leftPanelCollapsed }),
          false,
          'toggleLeftPanel'
        ),

        toggleRightPanel: () => set(
          (state) => ({ rightPanelCollapsed: !state.rightPanelCollapsed }),
          false,
          'toggleRightPanel'
        ),

        setLeftPanelSize: (size) => set(
          { leftPanelSize: Math.min(Math.max(size, PANEL_MIN_SIZE), PANEL_MAX_SIZE) },
          false,
          'setLeftPanelSize'
        ),

        setRightPanelSize: (size) => set(
          { rightPanelSize: Math.min(Math.max(size, PANEL_MIN_SIZE), PANEL_MAX_SIZE) },
          false,
          'setRightPanelSize'
        ),

        setViewMode: (mode) => set(
          { viewMode: mode },
          false,
          'setViewMode'
        ),

        resetPanelStates: () => set(
          {
            leftPanelCollapsed: false,
            rightPanelCollapsed: false,
            leftPanelSize: PANEL_DEFAULT_LEFT,
            rightPanelSize: PANEL_DEFAULT_RIGHT,
            viewMode: 'default',
            categoryCollapseStates: {
              matter: false,
              workbench: false,
              playbook: false,
              workflow: false,
            },
            sectionOrder: DEFAULT_SECTION_ORDER,
            focusedIssueId: null,
            focusedIssueContext: null,
            focusedFindingId: null,
            focusedFindingContext: null,
            focusedFindingClauseContext: null,
            focusedClauseContext: null,
            pendingIssuePrompt: null,
            pendingFindingPrompt: null,
            issueChatThreads: {} as Record<string, IssueChatMessage[]>,
            findingChatThreads: {} as Record<string, IssueChatMessage[]>,
            // Clear split-view state so a post-logout / matter-change session
            // doesn't rehydrate into a split layout with no documents.
            splitMode: 'none' as SplitMode,
            splitSecondaryDocumentId: null,
            preSplitLeftPanelCollapsed: false,
            rightPanelViewStack: [{ type: 'chat' }],
            activeRightPanelTab: 'chat' as const,
          },
          false,
          'resetPanelStates'
        ),

        toggleCategoryCollapse: (category) => set(
          (state) => ({
            categoryCollapseStates: {
              ...state.categoryCollapseStates,
              [category]: !state.categoryCollapseStates[category],
            },
          }),
          false,
          'toggleCategoryCollapse'
        ),

        // Task 213: Section reordering
        setSectionOrder: (order) => set(
          { sectionOrder: order },
          false,
          'setSectionOrder'
        ),

        resetSectionOrder: () => set(
          { sectionOrder: DEFAULT_SECTION_ORDER },
          false,
          'resetSectionOrder'
        ),

        // Task 214: Right panel actions
        toggleHistorySlideOut: () => set(
          (state) => ({
            rightPanelSections: {
              ...state.rightPanelSections,
              historySlideOutOpen: !state.rightPanelSections.historySlideOutOpen,
            },
          }),
          false,
          'toggleHistorySlideOut'
        ),

        setHistorySlideOutOpen: (open) => set(
          (state) => ({
            rightPanelSections: {
              ...state.rightPanelSections,
              historySlideOutOpen: open,
            },
          }),
          false,
          'setHistorySlideOutOpen'
        ),

        toggleQuickActionsCollapsed: () => set(
          (state) => ({
            rightPanelSections: {
              ...state.rightPanelSections,
              quickActionsCollapsed: !state.rightPanelSections.quickActionsCollapsed,
            },
          }),
          false,
          'toggleQuickActionsCollapsed'
        ),

        setActiveChatSession: (sessionId) => set(
          { activeChatSessionId: sessionId },
          false,
          'setActiveChatSession'
        ),

        setAvailableChatSessions: (sessions) => set(
          { availableChatSessions: sessions },
          false,
          'setAvailableChatSessions'
        ),

        // Task 214.1: Right panel navigation stack actions
        pushRightPanelView: (view) => set(
          (state) => {
            // Don't push duplicate view type on top
            const currentView = state.rightPanelViewStack[state.rightPanelViewStack.length - 1]
            const newStack = currentView?.type === view.type
              ? [...state.rightPanelViewStack.slice(0, -1), view]
              : [...state.rightPanelViewStack, view]
            return {
              rightPanelViewStack: newStack,
              // Auto-switch to analysis tab when pushing non-chat views
              activeRightPanelTab: view.type !== 'chat' ? 'analysis' as const : state.activeRightPanelTab,
            }
          },
          false,
          'pushRightPanelView'
        ),

        popRightPanelView: () => set(
          (state) => {
            if (state.rightPanelViewStack.length <= 1) {
              // Always keep at least chat as base
              return { rightPanelViewStack: [{ type: 'chat' }] }
            }
            return { rightPanelViewStack: state.rightPanelViewStack.slice(0, -1) }
          },
          false,
          'popRightPanelView'
        ),

        resetRightPanelToChat: () => set(
          {
            rightPanelViewStack: [{ type: 'chat' }],
            focusedIssueId: null,
            focusedIssueContext: null,
            focusedFindingId: null,
            focusedFindingContext: null,
            focusedFindingClauseContext: null,
            focusedClauseContext: null,
            activeRightPanelTab: 'chat',
          },
          false,
          'resetRightPanelToChat'
        ),

        replaceRightPanelView: (view) => set(
          (state) => {
            const newStack = [...state.rightPanelViewStack]
            newStack[newStack.length - 1] = view
            return { rightPanelViewStack: newStack }
          },
          false,
          'replaceRightPanelView'
        ),

        // Per-issue focus actions
        setActiveRightPanelTab: (tab) => set(
          { activeRightPanelTab: tab },
          false,
          'setActiveRightPanelTab'
        ),

        setFocusedIssue: (issueId, issueCtx, clauseCtx) => set(
          (state) => ({
            focusedIssueId: issueId,
            focusedIssueContext: issueCtx,
            focusedFindingId: null,
            focusedFindingContext: null,
            focusedFindingClauseContext: null,
            focusedClauseContext: clauseCtx,
            // Auto-switch to the analysis tab when focusing an issue. Do NOT
            // touch the view stack: renderAnalysisContent checks focusedIssueId
            // first (RightPanelContainer.tsx:108), so IssueChatThread takes
            // precedence while focused; leaving the stack intact means
            // unfocusing reveals whatever was underneath (typically
            // ClauseActionsView) instead of an empty placeholder.
            activeRightPanelTab: issueId ? 'analysis' as const : state.activeRightPanelTab,
          }),
          false,
          'setFocusedIssue'
        ),

        setFocusedFinding: (findingId, findingCtx, clauseCtx) => set(
          (state) => ({
            focusedFindingId: findingId,
            focusedFindingContext: findingCtx,
            focusedFindingClauseContext: clauseCtx,
            focusedIssueId: null,
            focusedIssueContext: null,
            // See setFocusedIssue — do not touch the view stack on focus so
            // unfocus reveals the underlying view (e.g. ClauseActionsView).
            activeRightPanelTab: findingId ? 'analysis' as const : state.activeRightPanelTab,
          }),
          false,
          'setFocusedFinding'
        ),

        // Plan 41: Clear stale right-pane state after a finding is dismissed.
        // Covers all four render paths that can hold a stale reference:
        //   (A) focusedIssueId + focusedIssueContext → IssueChatThread
        //   (B) focusedFindingId + focusedFindingContext → FindingChatThread
        //   (C) rightPanelViewStack frames of type issueDetail / changeDetail
        //       / negotiationDetail referencing the dismissed id (anywhere
        //       in the stack — pushRightPanelView dedup is top-only)
        //   (D) issueList frames whose embedded issueListContext.issues
        //       array contains the dismissed issue (IssueListView reads
        //       from that snapshot, including its scoped-chat LLM prompt)
        // Buried redraft frames bound to the dismissed id are also dropped
        // — they would re-fire generateFix on remount (RedraftView.tsx
        // useEffect at :113-145 / :147-166). The current top redraft is
        // preserved (user work-in-progress they can see right now).
        // Uses get() + conditional set() so no-match calls write nothing
        // (Zustand fires listeners even when partials are empty).
        clearRightPaneForDismissedFinding: (kind, id) => {
          const state = get();
          const changes: Partial<DeskStore> = {};

          // (A) Focused-issue
          if (kind === 'issue' && state.focusedIssueId === id) {
            changes.focusedIssueId = null;
            changes.focusedIssueContext = null;
            changes.focusedClauseContext = null;
          }

          // (B) Focused-finding (compare)
          if (kind === 'compare' && state.focusedFindingId === id) {
            changes.focusedFindingId = null;
            changes.focusedFindingContext = null;
            changes.focusedFindingClauseContext = null;
          }

          // (C) + (D) + (E) Stack pass
          const lastIndex = state.rightPanelViewStack.length - 1;

          // Plan 235 / #967: issueDetail / changeDetail / issueList frames no
          // longer exist -- issues and compare findings both render through
          // their focused scoped chat, cleared by (A)/(B) above. Only
          // negotiationDetail remains as a stack frame.
          const isFindingDetailMatch = (view: RightPanelView): boolean => {
            if (kind === 'negotiation' && view.type === 'negotiationDetail') {
              return view.negotiationContext?.findingId === id;
            }
            return false;
          };

          const isBuriedRedraftMatch = (view: RightPanelView, index: number): boolean => {
            if (view.type !== 'redraft' || index === lastIndex) return false;
            const rc = view.redraftContext;
            if (kind === 'issue') return rc?.sourceIssueId === id;
            if (kind === 'compare') return rc?.sourceFindingId === id;
            return false; // negotiation: no RedraftContext binding in practice
          };

          const rewriteOrDrop = (view: RightPanelView, index: number): RightPanelView | null => {
            if (isFindingDetailMatch(view)) return null;
            if (isBuriedRedraftMatch(view, index)) return null;
            return view;
          };

          const nextStack: RightPanelView[] = [];
          let stackChanged = false;
          state.rightPanelViewStack.forEach((view, index) => {
            const rewritten = rewriteOrDrop(view, index);
            if (rewritten === null) {
              stackChanged = true;
              return;
            }
            if (rewritten !== view) stackChanged = true;
            nextStack.push(rewritten);
          });
          // popRightPanelView's invariant: stack always has at least chat.
          if (nextStack.length === 0) {
            nextStack.push({ type: 'chat' });
            stackChanged = true;
          }
          if (stackChanged) changes.rightPanelViewStack = nextStack;

          if (Object.keys(changes).length === 0) return;
          set(changes, false, 'clearRightPaneForDismissedFinding');
        },

        sendIssuePrompt: (prompt) => {
          // [#769-trace] Every store-write to pendingIssuePrompt. Compare count
          // to PromptChips dispatch logs — should be 1:1.
          // eslint-disable-next-line no-console
          console.log('[#769-trace] deskStore.sendIssuePrompt SET', {
            t: performance.now(),
            traceId: (prompt as any).__traceId,
          });
          return set(
            { pendingIssuePrompt: prompt },
            false,
            'sendIssuePrompt'
          );
        },

        clearPendingIssuePrompt: () => {
          // [#769-trace] Every clearPendingIssuePrompt store-write. Should be
          // 1:1 with effect-handled sends; if we see this called multiple
          // times before a render flush, the useEffect is being re-entered.
          // eslint-disable-next-line no-console
          console.log('[#769-trace] deskStore.clearPendingIssuePrompt CALL', {
            t: performance.now(),
          });
          return set(
            { pendingIssuePrompt: null },
            false,
            'clearPendingIssuePrompt'
          );
        },

        addIssueChatMessage: (issueId, message) => set(
          (state) => ({
            issueChatThreads: {
              ...state.issueChatThreads,
              [issueId]: [...(state.issueChatThreads[issueId] || []), message],
            },
          }),
          false,
          'addIssueChatMessage'
        ),

        setIssueChatThread: (issueId, messages) => set(
          (state) => ({
            issueChatThreads: {
              ...state.issueChatThreads,
              [issueId]: messages,
            },
          }),
          false,
          'setIssueChatThread'
        ),

        sendFindingPrompt: (prompt) => set(
          { pendingFindingPrompt: prompt },
          false,
          'sendFindingPrompt'
        ),

        clearPendingFindingPrompt: () => set(
          { pendingFindingPrompt: null },
          false,
          'clearPendingFindingPrompt'
        ),

        addFindingChatMessage: (findingId, message) => set(
          (state) => ({
            findingChatThreads: {
              ...state.findingChatThreads,
              [findingId]: [...(state.findingChatThreads[findingId] || []), message],
            },
          }),
          false,
          'addFindingChatMessage'
        ),

        setFindingChatThread: (findingId, messages) => set(
          (state) => ({
            findingChatThreads: {
              ...state.findingChatThreads,
              [findingId]: messages,
            },
          }),
          false,
          'setFindingChatThread'
        ),

        // Plan 143: per-clause chat threads.
        addClauseChatMessage: (clauseId, message) => set(
          (state) => ({
            clauseChatThreads: {
              ...state.clauseChatThreads,
              [clauseId]: [...(state.clauseChatThreads[clauseId] || []), message],
            },
          }),
          false,
          'addClauseChatMessage'
        ),

        setClauseChatThread: (clauseId, messages) => set(
          (state) => ({
            clauseChatThreads: {
              ...state.clauseChatThreads,
              [clauseId]: messages,
            },
          }),
          false,
          'setClauseChatThread'
        ),

        acceptRedraft: (clauseId, newText, revisionId, acceptedAt) => set(
          (state) => ({
            acceptedRedrafts: {
              ...state.acceptedRedrafts,
              [clauseId]: [
                ...(state.acceptedRedrafts[clauseId] || []),
                // Issue #771 follow-up: track acceptedAt so the ClauseCard
                // "Analysis based on prior version of this clause" caption
                // (Plan 118) can compare against issue.created_at and only
                // surface when the redraft truly post-dates the analysis.
                { text: newText, revisionId, acceptedAt: acceptedAt ?? new Date().toISOString() },
              ],
            },
          }),
          false,
          'acceptRedraft'
        ),

        revertRedraft: (clauseId) => set(
          (state) => {
            const stack = state.acceptedRedrafts[clauseId];
            if (!stack || stack.length === 0) return state;
            const newStack = stack.slice(0, -1);
            const newRedrafts = { ...state.acceptedRedrafts };
            if (newStack.length === 0) {
              delete newRedrafts[clauseId];
            } else {
              newRedrafts[clauseId] = newStack;
            }
            return { acceptedRedrafts: newRedrafts };
          },
          false,
          'revertRedraft'
        ),

        revertToVersion: (clauseId, versionIndex) => set(
          (state) => {
            const newRedrafts = { ...state.acceptedRedrafts };
            if (versionIndex < 0) {
              delete newRedrafts[clauseId];
            } else {
              newRedrafts[clauseId] = (state.acceptedRedrafts[clauseId] || []).slice(0, versionIndex + 1);
            }
            return { acceptedRedrafts: newRedrafts };
          },
          false,
          'revertToVersion'
        ),

        updateRedraftStatus: (issueId, messageId, status) => set(
          (state) => {
            // Check both issue and finding threads
            const issueThread = state.issueChatThreads[issueId];
            const findingThread = state.findingChatThreads[issueId];
            const result: any = {};
            if (issueThread) {
              result.issueChatThreads = {
                ...state.issueChatThreads,
                [issueId]: issueThread.map((m) =>
                  m.id === messageId ? { ...m, redraftStatus: status } : m
                ),
              };
            }
            if (findingThread) {
              result.findingChatThreads = {
                ...state.findingChatThreads,
                [issueId]: findingThread.map((m) =>
                  m.id === messageId ? { ...m, redraftStatus: status } : m
                ),
              };
            }
            return result;
          },
          false,
          'updateRedraftStatus'
        ),

        // Task 232: Split pane actions
        setSplitMode: (mode) => set(
          (state) => {
            if (mode !== 'none' && state.splitMode === 'none') {
              // Entering split mode - save left panel state and collapse it
              return {
                splitMode: mode,
                preSplitLeftPanelCollapsed: state.leftPanelCollapsed,
                leftPanelCollapsed: true,
                focusedPane: 'primary',
              }
            }
            // Switching between split modes
            return { splitMode: mode }
          },
          false,
          'setSplitMode'
        ),

        setSplitSecondaryDocument: (documentId) => set(
          { splitSecondaryDocumentId: documentId },
          false,
          'setSplitSecondaryDocument'
        ),

        setDocumentViewMode: (mode) => set(
          { documentViewMode: mode },
          false,
          'setDocumentViewMode'
        ),

        exitSplitMode: () => set(
          (state) => ({
            splitMode: 'none' as SplitMode,
            splitSecondaryDocumentId: null,
            leftPanelCollapsed: state.preSplitLeftPanelCollapsed,
            focusedPane: 'primary' as const,
          }),
          false,
          'exitSplitMode'
        ),

        // Issue #353: Set which pane receives tab clicks
        setFocusedPane: (pane) => set(
          { focusedPane: pane },
          false,
          'setFocusedPane'
        ),

        // Task 243: Compare side-by-side - opens split view with base/review documents
        enterCompareSideBySide: (baseDocId, reviewDocId) => set(
          (state) => ({
            splitMode: 'side-by-side' as SplitMode,
            splitSecondaryDocumentId: reviewDocId,
            preSplitLeftPanelCollapsed: state.splitMode === 'none' ? state.leftPanelCollapsed : state.preSplitLeftPanelCollapsed,
            leftPanelCollapsed: true,
          }),
          false,
          'enterCompareSideBySide'
        ),

        // Task 487: Negotiation view mode action
        setNegotiationViewMode: (mode) => set(
          { negotiationViewMode: mode },
          false,
          'setNegotiationViewMode'
        ),

        // Task 233.1: Clause cards filter action
        setClauseCardsFilter: (filter) => set(
          { clauseCardsFilter: filter },
          false,
          'setClauseCardsFilter'
        ),

        // Task 234: Display options actions
        setDisplayOption: (key, value) => set(
          (state) => ({
            displayOptions: { ...state.displayOptions, [key]: value },
          }),
          false,
          'setDisplayOption'
        ),

        resetDisplayOptions: () => set(
          { displayOptions: DEFAULT_DISPLAY_OPTIONS },
          false,
          'resetDisplayOptions'
        ),

        // Plan 110: Playbook inspector sticky toggle
        togglePlaybookInspector: () => set(
          (state) => ({ playbookInspectorLocked: !state.playbookInspectorLocked }),
          false,
          'togglePlaybookInspector'
        ),

        // Plan 110: Playbook compare results
        setPlaybookCompareResult: (clauseId, result) => set(
          (state) => ({
            playbookCompareResults: { ...state.playbookCompareResults, [clauseId]: result },
          }),
          false,
          'setPlaybookCompareResult'
        ),
        clearPlaybookCompareResult: (clauseId) => set(
          (state) => {
            const { [clauseId]: _, ...rest } = state.playbookCompareResults;
            return { playbookCompareResults: rest };
          },
          false,
          'clearPlaybookCompareResult'
        ),
        addPendingPlaybookComment: (clauseId, entry) => set(
          (state) => ({
            pendingPlaybookComments: {
              ...state.pendingPlaybookComments,
              [clauseId]: { ...entry, createdAt: new Date().toISOString() },
            },
          }),
          false,
          'addPendingPlaybookComment'
        ),
        removePendingPlaybookComment: (clauseId) => set(
          (state) => {
            const { [clauseId]: _, ...rest } = state.pendingPlaybookComments;
            return { pendingPlaybookComments: rest };
          },
          false,
          'removePendingPlaybookComment'
        ),

        // Task 235: Apply layout preset - adjusts panel states
        applyLayout: (mode) => set(
          () => {
            switch (mode) {
              case 'focus':
                // Focus: collapse left panel, keep right panel for chat
                return {
                  viewMode: mode,
                  leftPanelCollapsed: true,
                  rightPanelCollapsed: false,
                }
              case 'simplified':
                // Simplified: collapse both panels, maximum document area
                return {
                  viewMode: mode,
                  leftPanelCollapsed: true,
                  rightPanelCollapsed: true,
                }
              case 'default':
              default:
                // Default: restore standard 3-panel layout
                return {
                  viewMode: 'default' as const,
                  leftPanelCollapsed: false,
                  rightPanelCollapsed: false,
                }
            }
          },
          false,
          'applyLayout'
        ),
      }),
      {
        name: 'taylaw-desk-state',
        partialize: (state) => ({
          // Persist user ID to detect stale state from a different user
          persistedUserId: state.persistedUserId,
          // Persist selected matter so document tabs have context on refresh
          selectedMatterId: state.selectedMatterId,
          leftPanelCollapsed: state.leftPanelCollapsed,
          rightPanelCollapsed: state.rightPanelCollapsed,
          leftPanelSize: state.leftPanelSize,
          rightPanelSize: state.rightPanelSize,
          viewMode: state.viewMode,
          // Persist split view state so side-by-side/top-down survives refresh
          splitMode: state.splitMode,
          splitSecondaryDocumentId: state.splitSecondaryDocumentId,
          preSplitLeftPanelCollapsed: state.preSplitLeftPanelCollapsed,
          // Task 232: Persist document view mode preference
          documentViewMode: state.documentViewMode,
          categoryCollapseStates: state.categoryCollapseStates,
          sectionOrder: state.sectionOrder,
          // Task 214: Persist right panel section preferences (but not historySlideOutOpen - should start closed)
          rightPanelSections: {
            historySlideOutOpen: false, // Always start closed
            quickActionsCollapsed: state.rightPanelSections.quickActionsCollapsed,
          },
          // Task 233.1: Persist clause cards filter
          clauseCardsFilter: state.clauseCardsFilter,
          // Task 234: Persist display options
          displayOptions: state.displayOptions,
        }),
      }
    ),
    { name: 'DeskStore' }
  )
)

// Selector helpers for common patterns
export const deskSelectors = {
  panelStates: (state: DeskStore) => ({
    leftCollapsed: state.leftPanelCollapsed,
    rightCollapsed: state.rightPanelCollapsed,
    leftSize: state.leftPanelSize,
    rightSize: state.rightPanelSize,
  }),

  selectedMatter: (state: DeskStore) => state.selectedMatterId,

  activeAnalysisRun: (state: DeskStore) => state.activeAnalysisRunId,

  viewMode: (state: DeskStore) => state.viewMode,

  // Task 232: Split pane selectors
  splitMode: (state: DeskStore) => state.splitMode,
  splitSecondaryDocumentId: (state: DeskStore) => state.splitSecondaryDocumentId,
  documentViewMode: (state: DeskStore) => state.documentViewMode,
  isSplitActive: (state: DeskStore) => state.splitMode !== 'none',
  focusedPane: (state: DeskStore) => state.focusedPane,

  // Task 487: Negotiation view mode selector
  negotiationViewMode: (state: DeskStore) => state.negotiationViewMode,

  // Task 233.1: Clause cards filter selector
  clauseCardsFilter: (state: DeskStore) => state.clauseCardsFilter,

  // Task 234: Display options selector
  displayOptions: (state: DeskStore) => state.displayOptions,

  // Task 214.1: Right panel navigation stack selectors
  currentRightPanelView: (state: DeskStore): RightPanelView =>
    state.rightPanelViewStack[state.rightPanelViewStack.length - 1] || { type: 'chat' },

  rightPanelViewType: (state: DeskStore): RightPanelViewType =>
    (state.rightPanelViewStack[state.rightPanelViewStack.length - 1]?.type) || 'chat',

  canPopRightPanel: (state: DeskStore): boolean =>
    state.rightPanelViewStack.length > 1,

  // Issue #844: which surface the Analysis tab renders — the focused-issue
  // chat, the focused-finding chat, or the top of the view stack.
  //
  // A focused issue/finding normally renders OVER the stack: setFocusedIssue /
  // setFocusedFinding deliberately leave the stack untouched so unfocusing
  // reveals the underlying view (e.g. ClauseActionsView). The one exception is
  // an Align staging view launched FROM the focused-finding chat —
  // FindingChatThread.handleAlignFromFinding pushes { type: 'alignStaging' }
  // without clearing focusedFindingId. Without this carve-out the
  // 'findingChat' branch masks the pushed view and the "Align to the other
  // clause" chip appears to do nothing. Scoped to the alignStaging whose
  // target IS the focused finding, so an unrelated align (e.g. opened from
  // ChangeDetailView for another finding) can't mask a finding the user just
  // clicked. Popping the alignStaging restores the chat because the focus is
  // still set.
  analysisRenderTarget: (state: DeskStore): 'issueChat' | 'findingChat' | 'stack' => {
    const top = state.rightPanelViewStack[state.rightPanelViewStack.length - 1] || { type: 'chat' };
    const alignTarget = top.type === 'alignStaging' ? top.alignContext?.target : undefined;
    const alignStagingForFocusedFinding =
      alignTarget?.source === 'compare_finding' &&
      state.focusedFindingId != null &&
      alignTarget.findingId === state.focusedFindingId;

    if (state.focusedIssueId && state.focusedIssueContext) return 'issueChat';
    if (state.focusedFindingId && state.focusedFindingContext && !alignStagingForFocusedFinding) {
      return 'findingChat';
    }
    return 'stack';
  },

  // Per-issue focus selectors
  focusedIssueId: (state: DeskStore) => state.focusedIssueId,
  focusedIssueContext: (state: DeskStore) => state.focusedIssueContext,
  focusedFindingId: (state: DeskStore) => state.focusedFindingId,
  focusedFindingContext: (state: DeskStore) => state.focusedFindingContext,
  focusedFindingClauseContext: (state: DeskStore) => state.focusedFindingClauseContext,
  focusedClauseContext: (state: DeskStore) => state.focusedClauseContext,
  activeRightPanelTab: (state: DeskStore) => state.activeRightPanelTab,

  // Plan 110: Playbook inspector
  playbookInspectorLocked: (state: DeskStore) => state.playbookInspectorLocked,
}

// Panel size constraints exported for use in components
export const PANEL_CONSTRAINTS = {
  MIN_SIZE: 10,
  MAX_SIZE: 35,
  DEFAULT_LEFT: 18,
  DEFAULT_RIGHT: 20,
}
